import React, { useState, useEffect, useRef } from "react";
import { FaArrowRight } from "react-icons/fa6";
import { FaArrowRightLong } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

const Cultivate: React.FC = () => {
  const { t } = useTranslation();

  // Text content
  const greenhouseText = t("cultivate_greenhouse");
  const cultivateText = t("cultivate_success");
  const innovationText = t("cultivate_innovation");

  // States
  const [greenhouseDisplayedText, setGreenhouseDisplayedText] =
    useState<string>("");
  const [cultivateDisplayedText, setCultivateDisplayedText] =
    useState<string>("");
  const [innovationDisplayedText, setInnovationDisplayedText] =
    useState<string>("");

  // Refs
  const greenhouseHeadingRef = useRef<HTMLDivElement | null>(null);
  const cultivateHeadingRef = useRef<HTMLDivElement | null>(null);
  const innovationHeadingRef = useRef<HTMLDivElement | null>(null);

  // Typewriter Hook
  const useTypewriter = (
    ref: React.RefObject<HTMLDivElement | null>,
    text: string,
    setText: React.Dispatch<React.SetStateAction<string>>,
    delay: number = 50
  ) => {
    useEffect(() => {
      if (!ref.current) return;

      let observer: IntersectionObserver | null = null;
      let interval: ReturnType<typeof setInterval> | null = null;

      const handleIntersect = (entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) {
          let i = 0;
          interval = setInterval(() => {
            setText(text.slice(0, i + 1));
            i++;
            if (i === text.length && interval) clearInterval(interval);
          }, delay);

          observer?.disconnect();
        }
      };

      observer = new IntersectionObserver(handleIntersect, {
        threshold: 0.4,
      });

      observer.observe(ref.current);

      return () => {
        if (interval) clearInterval(interval);
        observer?.disconnect();
      };
    }, [text, ref, setText, delay]);
  };

  // Apply typewriter
  useTypewriter(
    greenhouseHeadingRef,
    greenhouseText,
    setGreenhouseDisplayedText
  );
  useTypewriter(cultivateHeadingRef, cultivateText, setCultivateDisplayedText);
  useTypewriter(
    innovationHeadingRef,
    innovationText,
    setInnovationDisplayedText
  );

  return (
    <div className="w-full min-h-screen bg-white flex flex-col  lg:flex-row items-center p-4 md:p-8 gap-8 ">
      {/* Greenhouse Card */}
      <div className="w-full lg:w-[35%]  flex items-center justify-center">
        <div className="bg-[#002F3F] w-full max-w-md rounded-2xl p-6">
          <p className="text-white text-sm">{t("cultivate_news_date")}</p>

          <div ref={greenhouseHeadingRef} className="min-h-[3rem] mt-4">
            <h1 className="text-white text-2xl font-semibold" aria-label={greenhouseText}>
              {greenhouseDisplayedText || "\u00A0"}
            </h1>
          </div>

          <img
            src="/cultivate1.webp"
                loading="lazy"
            alt="Greenhouse"
            className="mt-4 w-full h-48 rounded-xl object-cover transition duration-300 hover:scale-105"
          />

          <p className="text-white mt-4 text-sm sm:text-lg leading-relaxed">
            {t("cultivate_description")}
          </p>

          <Link
            to="/projects"
             aria-label="Explore greenhouse projects"
            className="mt-6 w-12 h-12 flex items-center justify-center  rounded-2xl bg-[#7EDE56] shadow-lg hover:bg-[#4fc93d] transition"
          >
            <FaArrowRight className="text-[#002F3F] w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Right Section */}
      <div className="w-full lg:w-[65%] flex flex-col">
        <div ref={cultivateHeadingRef} className="min-h-[4rem]">
          <h1 className="text-5xl font-bold text-[#7EDE56]" aria-label={cultivateText}>
            {cultivateDisplayedText || "\u00A0"}
          </h1>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div ref={innovationHeadingRef} className="min-h-[4rem]">
            <h1 className="text-[#002F3F] text-5xl font-extrabold" aria-label={innovationText}>
              {innovationDisplayedText || "\u00A0"}
            </h1>
          </div>

          <Link
            to="/projects"
            aria-label="Explore greenhouse projects"
            className="px-6 py-3 rounded-lg bg-[#7EDE56] text-[#002F3F] hover:bg-[#002F3F] hover:text-white transition flex items-center gap-2"
          >
            {t("cultivate_explore")}
              <FaArrowRightLong/>
          </Link>
        </div>

        {/* Bottom Section */}
        <div className="mt-6 w-full flex flex-col lg:flex-row gap-6">
          {/* Turnkey */}
          <div className="w-full lg:w-1/2">
            <img
              src="/home-turn-key.webp"
                loading="lazy"
              alt="home-turn-key"
              className="w-full h-52 rounded-2xl object-cover transition hover:scale-105"
            />
            <p className="text-gray-600 mt-2 text-sm">Monday, June 3, 2024</p>
            <p className="text-[#002F3F] text-xl font-semibold mt-1">
              {t("cultivate_turnkey")}
            </p>
            <Link
              to="/turnkey-projects"
              aria-label="Explore turnkey greenhouse projects"
              className="mt-3 px-4 py-2 border border-[#002F3F] text-[#002F3F] rounded-full hover:bg-[#002F3F] hover:text-white transition flex items-center justify-center gap-2">
              <FaArrowRight />
            </Link>
          </div>

          {/* Customized */}
          <div className="w-full lg:w-1/2">
            <img
              src="/box-slider4.webp"
                loading="lazy"
              alt="customized-solution"
              className="w-full h-52 rounded-2xl object-cover transition hover:scale-105"
            />
            <p className="text-gray-600 mt-2 text-sm">Monday, June 3, 2024</p>
            <p className="text-[#002F3F] text-xl font-semibold mt-1">
              {t("cultivate_customized")}
            </p>
            <Link to="/customize-solution"
              aria-label="Explore customized solutions"
              className="mt-3 px-4 py-2 border border-[#002F3F] text-[#002F3F] rounded-full hover:bg-[#002F3F] hover:text-white transition flex items-center justify-center gap-2">
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cultivate;
